import React, { useMemo } from "react";
import { useParams } from "react-router-dom";
import {
  CartesianGrid,
  Cell,
  Legend,
  Line,
  LineChart,
  Pie,
  PieChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";
import { Tile, TileGrid, PreviewBanner } from "@/components/common/Tile";
import { Loading } from "@/components/common/Loading";
import { Pathogen, formatCopies } from "@/components/common/Pathogen";
import { useDevice } from "@/hooks/useDevice";
import { useOpticsForDevice, useOptic } from "@/hooks/useOptic";
import { SampleSet } from "@/api/qpcr";

const COLORS = [
  "#2563EB", "#DC2626", "#7C3AED", "#059669",
  "#CA8A04", "#0EA5E9", "#DB2777", "#65A30D",
];

const DOUGHNUT_COLORS = ["#DC2626", "#2563EB"];

function fmtDateTime(iso: string | null | undefined): string {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleString();
  } catch {
    return "—";
  }
}

function Chip({
  label,
  variant,
}: {
  label: React.ReactNode;
  variant: "online" | "offline" | "idle" | "info";
}) {
  const styles: Record<string, string> = {
    online: "bg-success-bg text-success-text",
    offline: "bg-danger-bg text-danger-text",
    idle: "bg-purple-bg text-purple-text",
    info: "bg-info-bg text-info-text",
  };
  return (
    <span className={`inline-flex items-center px-sm py-[2px] rounded-md text-xs font-medium ${styles[variant]}`}>
      {label}
    </span>
  );
}

function KV({ title, value, last }: { title: string; value: React.ReactNode; last?: boolean }) {
  return (
    <>
      <div className="flex items-center justify-between py-xs">
        <span className="text-sm font-medium text-text">{title}</span>
        <span className="text-sm text-textLight">{value}</span>
      </div>
      {!last && <div className="border-b border-border" />}
    </>
  );
}

function CalendarGrid() {
  const cells = useMemo(() => {
    const now = new Date();
    const year = now.getFullYear();
    const month = now.getMonth();
    const firstOfMonth = new Date(year, month, 1);
    const start = new Date(firstOfMonth);
    start.setDate(start.getDate() - start.getDay()); // back to Sunday
    const lastOfMonth = new Date(year, month + 1, 0);
    const end = new Date(lastOfMonth);
    end.setDate(end.getDate() + (6 - end.getDay()));
    const days: { date: Date; inMonth: boolean; isToday: boolean }[] = [];
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const cur = new Date(start);
    while (cur <= end) {
      const d = new Date(cur);
      days.push({
        date: d,
        inMonth: d.getMonth() === month,
        isToday: d.getTime() === today.getTime(),
      });
      cur.setDate(cur.getDate() + 1);
    }
    return days;
  }, []);

  const dayHeaders = ["S", "M", "T", "W", "T", "F", "S"];

  return (
    <div className="w-full">
      <div className="grid grid-cols-7 gap-xs mb-xs">
        {dayHeaders.map((d, i) => (
          <div key={i} className="text-center text-xs text-textLight font-medium">{d}</div>
        ))}
      </div>
      <div className="grid grid-cols-7 gap-xs">
        {cells.map((c, i) => (
          <div
            key={i}
            className={`aspect-square flex items-center justify-center rounded-md text-sm ${
              c.inMonth ? "text-text" : "text-textLight/40"
            } ${c.isToday ? "font-bold bg-info-bg text-info-text" : ""}`}
          >
            {c.date.getDate()}
          </div>
        ))}
      </div>
    </div>
  );
}

function ReloadDoughnut({ consumed }: { consumed: number }) {
  const data = [
    { name: "Consumed", value: consumed },
    { name: "Available", value: 100 - consumed },
  ];
  return (
    <div className="h-56 w-full">
      <ResponsiveContainer width="100%" height="100%">
        <PieChart>
          <Pie
            data={data}
            dataKey="value"
            nameKey="name"
            innerRadius={55}
            outerRadius={85}
            paddingAngle={2}
            isAnimationActive={false}
          >
            {data.map((_, i) => (
              <Cell key={i} fill={DOUGHNUT_COLORS[i]} />
            ))}
          </Pie>
          <Tooltip contentStyle={{ fontSize: 12 }} />
          <Legend verticalAlign="bottom" align="center" wrapperStyle={{ fontSize: 11, paddingTop: 18 }} />
        </PieChart>
      </ResponsiveContainer>
    </div>
  );
}

function RfuChart({ sampleSet }: { sampleSet: SampleSet }) {
  const cycles = Math.max(
    sampleSet.control_positive.length,
    sampleSet.control_ntc.length,
    ...sampleSet.wells.map(w => w.length),
    0,
  );
  const data: Record<string, number | null>[] = [];
  for (let c = 0; c < cycles; c++) {
    const point: Record<string, number | null> = { cycle: c + 1 };
    point["control+"] = sampleSet.control_positive[c] ?? null;
    point["control-"] = sampleSet.control_ntc[c] ?? null;
    sampleSet.wells.forEach((well, i) => {
      point[`replicate ${i + 1}`] = well[c] ?? null;
    });
    data.push(point);
  }
  const seriesNames = [
    "control+",
    "control-",
    ...sampleSet.wells.map((_, i) => `replicate ${i + 1}`),
  ];
  return (
    <div className="h-80 w-full">
      <ResponsiveContainer width="100%" height="100%">
        <LineChart data={data} margin={{ top: 8, right: 12, bottom: 56, left: 8 }}>
          <CartesianGrid strokeDasharray="3 3" stroke="#E5E7EB" />
          <XAxis
            dataKey="cycle"
            stroke="#6B7280"
            fontSize={11}
            label={{ value: "Cycle Number", position: "insideBottom", offset: -10, fontSize: 11 }}
          />
          <YAxis
            stroke="#6B7280"
            fontSize={11}
            label={{ value: "RFU", angle: -90, position: "insideLeft", fontSize: 11 }}
          />
          <Tooltip contentStyle={{ fontSize: 12 }} />
          <Legend verticalAlign="bottom" align="center" wrapperStyle={{ fontSize: 11, paddingTop: 18 }} />
          {seriesNames.map((name, idx) => (
            <Line
              key={name}
              type="monotone"
              dataKey={name}
              stroke={COLORS[idx % COLORS.length]}
              strokeDasharray={name.startsWith("control") ? "4 2" : undefined}
              dot={false}
              isAnimationActive={false}
              connectNulls={false}
            />
          ))}
        </LineChart>
      </ResponsiveContainer>
    </div>
  );
}

function quantificationFor(ss: SampleSet): number | null {
  // No quantification field on SampleSet — derive a placeholder from max well value.
  const all = ss.wells.flat().filter((n): n is number => typeof n === "number");
  if (all.length === 0) return null;
  return Math.round(Math.max(...all) * 100);
}

export function Dashboard() {
  const { deviceId = "" } = useParams<{ deviceId: string }>();
  const deviceQ = useDevice(deviceId);
  const opticsQ = useOpticsForDevice(deviceId, 1);
  const latestOpticId = opticsQ.data?.optics?.[0]?.id ?? "";
  const opticQ = useOptic(latestOpticId);

  if (deviceQ.isLoading || opticsQ.isLoading) {
    return <Loading />;
  }
  const device = deviceQ.data;
  if (!device) {
    return <div className="text-textLight text-sm">Device not found.</div>;
  }

  const latestOptic = latestOpticId ? opticQ.data : null;
  const sampleSets = latestOptic?.sample_sets ?? [];
  const primarySampleSet = sampleSets[0];

  return (
    <TileGrid>
      {/* Device Status — xs=4 */}
      <div className="col-span-12 md:col-span-4">
        <Tile title="Device Status">
          <div className="flex flex-wrap gap-xs mb-md">
            {device.label && <Chip label={device.label} variant="info" />}
            {device.is_online ? (
              <Chip label="Online" variant="online" />
            ) : (
              <Chip label="Offline" variant="offline" />
            )}
            <Chip label="Idle" variant="idle" />
          </div>
          <div className="flex flex-col">
            <KV title="Last Seen" value={fmtDateTime(device.last_seen_at)} />
            <KV title="Last Reload" value="—" />
            <KV
              title="Last Test"
              value={fmtDateTime(latestOptic?.captured_at ?? latestOptic?.received_at ?? opticsQ.data?.optics?.[0]?.captured_at ?? opticsQ.data?.optics?.[0]?.received_at)}
            />
            <KV title="Last Cleaning" value="—" last />
          </div>
        </Tile>
      </div>

      {/* Reload Status — xs=8 */}
      <div className="col-span-12 md:col-span-8">
        <Tile title="Reload Status">
          <div className="text-xs text-textLight mb-xs">Current Reagent Consumption (%) · demo data</div>
          <ReloadDoughnut consumed={35} />
        </Tile>
      </div>

      {/* Latest Result — xs=8 */}
      <div className="col-span-12 md:col-span-8">
        <Tile title="Latest Result">
          {!latestOpticId ? (
            <div className="flex items-center justify-center min-h-[222px] text-sm text-textLight">
              No tests have been performed on this device
            </div>
          ) : opticQ.isLoading || !latestOptic ? (
            <Loading />
          ) : !primarySampleSet ? (
            <div className="flex items-center justify-center min-h-[222px] text-sm text-textLight">
              No sample data available for the latest test
            </div>
          ) : (
            <div className="flex flex-col lg:flex-row gap-md">
              <div className="flex-1 min-w-0">
                <div className="text-xs text-textLight mb-xs">
                  PCR Relative Fluorescence Curve (RFU vs. Cycle Number)
                </div>
                <RfuChart sampleSet={primarySampleSet} />
              </div>
              <div className="flex flex-col gap-sm lg:w-56">
                {sampleSets.map(ss => {
                  const q = quantificationFor(ss);
                  return (
                    <div
                      key={ss.id}
                      className="rounded-md border border-info-text/30 bg-info-bg text-info-text px-md py-sm"
                    >
                      <div className="text-sm font-medium">
                        <Pathogen name={ss.pathogen_name ?? `pathogen ${ss.pathogen_id ?? "?"}`} />
                      </div>
                      <div className="text-xs mt-xs">
                        {formatCopies(q)} copies/mL
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          )}
        </Tile>
      </div>

      {/* Upcoming — xs=4 */}
      <div className="col-span-12 md:col-span-4">
        <Tile title="Upcoming">
          <CalendarGrid />
        </Tile>
      </div>

      {/* Trends — xs=12 */}
      <div className="col-span-12">
        <Tile title="Trends">
          <div className="flex items-center justify-center py-lg text-sm text-textLight">
            Perform more tests to display trend analysis
          </div>
        </Tile>
      </div>
    </TileGrid>
  );
}
